import type { FaceMoneyDTO, GiftcardRateDTO, MoneyDTO } from '../contracts/types.js';

/**
 * Money formatting for the console.
 *
 * Amounts arrive as integer-minor-unit strings (the contract's `MoneyDTO`) and are
 * parsed with `BigInt` — never `Number` — so a ₦50,000,000.00 balance never loses a
 * kobo to float error. Naira/kobo is the common case; other currencies fall back to a
 * two-decimal minor unit, which is correct for every fiat the app settles in.
 */
/*
 * Minor-unit exponents, mirroring `packages/money`'s currency table.
 *
 * A hand mirror, because the console keeps no runtime dependency on `@genex/money` — pulling
 * it in would bundle the whole money package to render a number. `money.test.ts` holds this
 * against the real table, which is what a hand mirror needs and did not have.
 *
 * It did not have it, and it was wrong. USDT and USDC were 2 rather than 6, ETH was 8 rather
 * than 18, and SOL was missing entirely and fell through to the 2-decimal default. Every
 * crypto amount this console has ever displayed was therefore too large by a factor of ten
 * thousand, ten billion, or ten million respectively — on the transaction feed, on a
 * customer's balances, and on the balance-adjustment form an operator types into. It looks
 * exactly like a plausible number, which is why nobody caught it by reading the screen.
 */
const MINOR_UNITS: Readonly<Record<string, number>> = {
  NGN: 2,
  USD: 2,
  USDT: 6,
  USDC: 6,
  BTC: 8,
  ETH: 18,
  SOL: 9,
};

const SYMBOLS: Readonly<Record<string, string>> = {
  NGN: '₦',
  USD: '$',
};

function decimalsFor(currency: string): number {
  return MINOR_UNITS[currency] ?? 2;
}

/**
 * How many decimal places one currency has, for a caller that is parsing rather than
 * formatting.
 *
 * Exported because two treasury screens each kept their own copy of this table — `{ NGN: 2,
 * USDT: 6, BTC: 8, ETH: 18, SOL: 9 }`, written out twice, guarded by nothing and missing USDC
 * in both. That is the same hand-mirror mistake `money.test.ts` exists to catch, made two more
 * times in files the test does not look at. There is one table now, and the test holds it
 * against the ledger's.
 */
export function decimalsOf(currency: string): number {
  return decimalsFor(currency);
}

/** Format a `MoneyDTO` as a grouped decimal string with a symbol/code, e.g. `₦50,000.00`. */
export function formatMoney(money: MoneyDTO): string {
  const decimals = decimalsFor(money.currency);
  const negative = money.amount.startsWith('-');
  const digits = (negative ? money.amount.slice(1) : money.amount).padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals) || '0';
  const frac = decimals > 0 ? digits.slice(digits.length - decimals) : '';
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const symbol = SYMBOLS[money.currency];
  const body = frac ? `${grouped}.${frac}` : grouped;
  const label = symbol ? `${symbol}${body}` : `${body} ${money.currency}`;
  return negative ? `-${label}` : label;
}

/** True when a money value is exactly zero, whatever its currency. */
export function isZeroMoney(money: MoneyDTO): boolean {
  return /^-?0+$/.test(money.amount);
}

/** True when a money value is negative. */
export function isNegativeMoney(money: MoneyDTO): boolean {
  return money.amount.startsWith('-') && !isZeroMoney(money);
}

const FACE_SYMBOLS: Readonly<Record<string, string>> = { USD: '$', GBP: '£', EUR: '€', CAD: 'CA$', AUD: 'A$' };

/**
 * A gift-card rate, `{ numerator (kobo), denominator (whole face units) }`, rendered as
 * naira per one whole unit of the face currency, e.g. `₦1,200.00 per $1`. The division
 * stays exact-to-two-kobo: kobo-per-unit is `numerator / denominator`, formatted as NGN.
 */
export function formatGiftcardRate(rate: GiftcardRateDTO, faceCurrency: string): string {
  const num = BigInt(rate.numerator);
  const den = BigInt(rate.denominator);
  // kobo per unit, rounded to the nearest kobo (half-up) for a clean per-unit figure.
  const koboPerUnit = den === 0n ? 0n : (num + den / 2n) / den;
  const naira = formatMoney({ currency: 'NGN', amount: koboPerUnit.toString() });
  const sym = FACE_SYMBOLS[faceCurrency] ?? '';
  return `${naira} per ${sym}1`;
}

/**
 * Format a foreign-fiat face amount as `$50.00` / `£25.00`.
 *
 * Goes through `formatMoney` for the digits — same integer-minor-unit discipline, same
 * grouping — but takes its symbol from `FACE_SYMBOLS`, because the ledger's `SYMBOLS` map
 * only knows the currencies the ledger holds and a card's GBP or CAD face is not one of
 * them. Without this a £25 card rendered as `25.00 GBP` next to a rate that said `per £1`.
 */
export function formatFaceMoney(face: FaceMoneyDTO): string {
  const symbol = FACE_SYMBOLS[face.currency];
  if (symbol === undefined) return formatMoney(face as unknown as MoneyDTO);
  // Formatted as naira to borrow the two-decimal grouping, then re-symboled. Every face
  // fiat is a two-decimal currency (`FACE_DECIMALS`), so the digits are identical.
  const body = formatMoney({ currency: 'NGN', amount: face.amount }).replace('₦', '');
  return `${symbol}${body}`;
}

// ── Operator input → minor units ─────────────────────────────────────────────────
//
// The mirror of the formatters above, and the direction that actually matters: an
// operator types a major-unit figure (naira, dollars) and the API is only ever sent
// integer minor units. Everything below is `BigInt` and string surgery — no `Number`, no
// `parseFloat`, no `Math.round(value * 100)`. That last line is the specific one this
// exists to keep out of the codebase: `Math.round(20_100_000.07 * 100)` is off by a kobo,
// and a tier ceiling or a face bound that is off by a kobo is a customer refused a
// transaction the console says is allowed.

/**
 * Minor-unit decimals for each gift-card face currency. Mirrors the API's
 * `FACE_CURRENCY_DECIMALS`: every supported face fiat is a two-decimal one, but it is kept
 * explicit rather than assumed so adding a zero-decimal currency later is a deliberate
 * edit here rather than a silent 100× error in every payout.
 */
const FACE_DECIMALS: Readonly<Record<string, number>> = {
  USD: 2,
  GBP: 2,
  EUR: 2,
  CAD: 2,
  AUD: 2,
};

export function faceDecimalsOf(currency: string): number {
  return FACE_DECIMALS[currency] ?? 2;
}

/**
 * A major-unit decimal an operator typed → integer minor units, or null when what they
 * typed is not an amount.
 *
 * Accepts an optional thousands separator, because an operator reading ₦500,000.00 off
 * one screen will type it with commas into the next; they are stripped before the digits
 * are read, never interpreted. Refuses more fraction digits than the currency has, rather
 * than truncating them — silently dropping a digit off a ceiling is precisely the class of
 * mistake this whole file is written to prevent.
 *
 * Negative is refused outright: nothing this parses (a ceiling, a face bound) is ever
 * meaningfully below zero, and the API refuses one anyway.
 */
export function parseMinorUnits(input: string, decimals: number): bigint | null {
  const trimmed = input.trim().replace(/,/g, '');
  if (trimmed === '') return null;
  const match = /^(\d+)(?:\.(\d*))?$/.exec(trimmed);
  if (match === null) return null;

  const whole = match[1] ?? '';
  const frac = match[2] ?? '';
  if (frac.length > decimals) return null;

  const padded = frac.padEnd(decimals, '0');
  return BigInt(whole + padded);
}

/** Naira typed by an operator → kobo. The common case, named so call sites read plainly. */
export function parseNairaToKobo(input: string): bigint | null {
  return parseMinorUnits(input, 2);
}

/**
 * Integer minor units → the plain (ungrouped) decimal an input should hold.
 *
 * Ungrouped on purpose: this feeds a text input the operator will edit, and a value that
 * comes back with commas in it is one `parseMinorUnits` has to strip again on every
 * keystroke. `formatMoney` is the grouped, read-only rendering; this is the editable one.
 */
export function minorUnitsToInput(amount: string, decimals: number): string {
  const negative = amount.startsWith('-');
  const digits = (negative ? amount.slice(1) : amount).padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  const frac = decimals > 0 ? `.${digits.slice(digits.length - decimals)}` : '';
  return `${negative ? '-' : ''}${whole}${frac}`;
}
